/**
 * Display configuration types for board sort, grouping, and field visibility.
 * Used by DisplayPopover, useBoardDisplay hook, KanbanColumn, ListView, and IssueCard.
 */

/** Fields that can be used for sorting */
export type SortField =
  | 'manual'
  | 'priority'
  | 'created'
  | 'updated'
  | 'due_date'
  | 'title'
  | 'type'
  | 'story_points'
  | 'status'
  | 'completion';

export type SortDirection = 'asc' | 'desc';

export interface SortRule {
  field: SortField;
  direction: SortDirection;
}

/** Fields that can be toggled visible on kanban cards */
export type CardField =
  | 'id'
  | 'type'
  | 'priority'
  | 'labels'
  | 'assignee'
  | 'progress'
  | 'parent'
  | 'due_date'
  | 'story_points'
  | 'pr_link'
  | 'design_link';

/** Human-readable labels for card fields */
export const CARD_FIELD_LABELS: Record<CardField, string> = {
  id: 'Issue ID',
  type: 'Type Badge',
  priority: 'Priority',
  labels: 'Labels',
  assignee: 'Assignee',
  progress: 'Progress Bar',
  parent: 'Parent Issue',
  due_date: 'Due Date',
  story_points: 'Story Points',
  pr_link: 'PR Link',
  design_link: 'Design Link',
};

/** Human-readable labels for sort fields */
export const SORT_FIELD_LABELS: Record<SortField, string> = {
  manual: 'Manual',
  priority: 'Priority',
  status: 'Status',
  created: 'Created',
  updated: 'Updated',
  due_date: 'Due Date',
  title: 'Title',
  type: 'Type',
  story_points: 'Story Points',
  completion: 'Child Completion',
};

/** Fields available for grouping */
export type GroupField = 'status' | 'priority' | 'type' | 'assignee' | 'none';

export const GROUP_FIELD_LABELS: Record<GroupField, string> = {
  none: 'No grouping',
  status: 'Status',
  priority: 'Priority',
  type: 'Type',
  assignee: 'Assignee',
};

/** Full display configuration */
export interface DisplayConfig {
  sortRules: SortRule[];
  visibleFields: CardField[];
  groupBy: GroupField;
}

/** Default display configuration */
export const DEFAULT_DISPLAY_CONFIG: DisplayConfig = {
  sortRules: [{ field: 'manual', direction: 'asc' }],
  visibleFields: ['id', 'type', 'priority', 'labels', 'assignee', 'progress', 'parent'],
  groupBy: 'status',
};

/** All available card fields in default display order */
export const ALL_CARD_FIELDS: CardField[] = [
  'id', 'type', 'priority', 'assignee', 'labels',
  'progress', 'parent', 'due_date', 'story_points', 'pr_link', 'design_link',
];
